import styled, { css } from 'styled-components'
import { DayPicker } from 'react-day-picker'
import { forwardRef, useImperativeHandle } from 'react'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import { type DatePickerLogicDependencies, DatePickerLogic } from './logic'
import type { DateRange } from 'react-day-picker'
import 'react-day-picker/style.css'
import { TextField } from '../../../../firebase-functions/src/common/components/text-field'
import { FloatingPopout } from '~common/components/floating-popout'
import {
    HelpContent,
    HelpSection,
    HelpTitle,
    SubText,
} from '~common/components/how-to-filter'

/**
 * Converts a UTC date to a local date representing the same calendar day.
 * This is needed because react-day-picker interprets dates in local time.
 */
function UTCToLocalForDisplay(utcDate: Date): Date {
    const year = utcDate.getUTCFullYear()
    const month = utcDate.getUTCMonth()
    const day = utcDate.getUTCDate()
    return new Date(year, month, day)
}

/**
 * Converts a UTC DateRange to a local DateRange for display in react-day-picker.
 */
function UTCRangeToLocalForDisplay(
    range: DateRange | undefined,
): DateRange | undefined {
    if (!range || !range.from) {
        return undefined
    }

    const localFrom = UTCToLocalForDisplay(range.from)

    if (!range.to) {
        return { from: localFrom }
    }

    const localTo = UTCToLocalForDisplay(range.to)
    return {
        from: localFrom,
        to: localTo,
    }
}

// NEW: Define custom classNames for DayPicker (overrides defaults for easy targeting)
const customClassNames = {
    root: 'custom-rdp-root', // Root calendar container
    caption: 'custom-rdp-caption', // Month/year dropdown area
    nav: 'custom-rdp-nav', // Navigation buttons (prev/next)
    nav_button: 'custom-rdp-nav-btn', // Individual nav buttons
    table: 'custom-rdp-table', // The month grid
    head_row: 'custom-rdp-head-row', // Weekday headers
    day: 'custom-rdp-day', // Individual day cells
    day_selected: 'custom-rdp-day-selected', // Selected days
    day_range_middle: 'custom-rdp-day-range-middle', // Days in range (for range mode)
    day_disabled: 'custom-rdp-day-disabled', // Disabled days
    // Add more as needed, e.g., day_today: 'custom-rdp-day-today'
}

// NEW: Custom DayButton component for full styling control (add after customClassNames, around line 25)
interface DayButtonProps {
    isSelected?: boolean
    isInRange?: boolean
    isRangeStart?: boolean
    isRangeEnd?: boolean
    isDisabled?: boolean
    isToday?: boolean
    // Add other props as needed from DayPicker types
}

const StyledDayButton = styled.button<DayButtonProps>`
    border: 1px solid transparent;
    padding: ${({ theme }) => theme.spacing[2]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: pointer;
    font-weight: normal;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        background-color: ${({ theme }) => theme.colors.greyScale[300]};
    }

    ${({ isSelected, isRangeStart, isRangeEnd, isInRange, theme }) => {
        if (isSelected || isRangeStart || isRangeEnd) {
            return css`
                background-color: ${theme.colors.greyScale[500]} !important;
                color: white !important;
                font-weight: bold;
            `
        }
        if (isInRange) {
            return css`
                background-color: ${theme.colors.greyScale[300]} !important;
                color: ${theme.colors.text.primary} !important;
            `
        }
        return ''
    }}

    ${({ isDisabled, theme }) =>
        isDisabled &&
        css`
            background-color: transparent;
            color: ${theme.colors.greyScale[400]};
            cursor: not-allowed;
        `}

  ${({ isToday, theme }) =>
        isToday &&
        css`
            border-color: ${theme.colors.border.primary};
        `}
`

export interface DatePickerRef {
    getValue: () => DateRange | Date | undefined
}

export const DatePicker = forwardRef<
    DatePickerRef,
    DatePickerLogicDependencies
>((props, ref) => {
    const appContext = useAppContext()
    const { logic, state } = useLogic(DatePickerLogic, {
        ...props,
        ...appContext,
    })

    useImperativeHandle(ref, () => ({
        getValue: () => state.selectedRange,
    }))

    // Get the current year for year restrictions
    const currentYear = new Date().getFullYear()

    return (
        <DatePickerContainer width={props.width}>
            <FloatingPopout
                open={state.isOpen}
                onOpenChange={logic.setIsOpen}
                placement="bottom-start"
                offset={4}
                trigger={
                    <TextField
                        placeholder={props.placeholder || 'Date'}
                        editable={true}
                        onChange={logic.handleInputChange}
                        initActive={true}
                        initValue={state.textValue}
                        onConfirm={(dateString) =>
                            logic.handleConfirm(dateString)
                        }
                        label={props.label}
                        inputValidation={props.inputValidation}
                        icon={props.icon}
                        variant={props.variant}
                        disableOnBlur={props.disableOnBlur}
                        onSort={props.onSortClick}
                        onClear={(shiftKey) => logic.handleClear(shiftKey)}
                        sortDirection={props.sortDirection}
                        size={props.size || 'medium'}
                        activated={
                            props.variant === 'tableHeader' &&
                            state.textValue.length > 0
                        }
                        resizable={props.resizable}
                    />
                }
                content={
                    <CalendarContent>
                        {/* NEW: Wrapper for CSS variables – sets theme-based colors/spacing on the DayPicker */}
                        <CalendarWrapper>
                            {props.singleDate ? (
                                <DayPicker
                                    key={'daypicker-single'}
                                    mode="single"
                                    selected={
                                        state.selectedRange instanceof Date
                                            ? UTCToLocalForDisplay(
                                                  state.selectedRange,
                                              )
                                            : undefined
                                    }
                                    onSelect={(date) =>
                                        logic.handleCalendarSelect(
                                            date || undefined,
                                        )
                                    }
                                    fromYear={2020}
                                    toYear={currentYear}
                                    captionLayout="dropdown"
                                    numberOfMonths={1}
                                    showOutsideDays
                                    fixedWeeks
                                    month={
                                        state.selectedRange instanceof Date
                                            ? UTCToLocalForDisplay(
                                                  state.selectedRange,
                                              )
                                            : new Date()
                                    }
                                    classNames={customClassNames}
                                    components={{
                                        DayButton: ({
                                            children,
                                            day,
                                            modifiers,
                                            ...delegated
                                        }) => {
                                            const isSelected =
                                                !!modifiers?.selected
                                            const isRangeStart =
                                                !!modifiers?.range_start
                                            const isRangeEnd =
                                                !!modifiers?.range_end
                                            const isInRange =
                                                !!modifiers?.range_middle
                                            const isDisabled =
                                                !!modifiers?.disabled
                                            const isToday = !!modifiers?.today

                                            return (
                                                <StyledDayButton
                                                    {...delegated}
                                                    isSelected={isSelected}
                                                    isRangeStart={isRangeStart}
                                                    isRangeEnd={isRangeEnd}
                                                    isInRange={isInRange}
                                                    isDisabled={isDisabled}
                                                    isToday={isToday}
                                                >
                                                    {children}
                                                </StyledDayButton>
                                            )
                                        },
                                    }}
                                />
                            ) : (
                                <DayPicker
                                    key={'daypicker-range'}
                                    mode="range"
                                    selected={
                                        state.selectedRange &&
                                        'from' in state.selectedRange
                                            ? UTCRangeToLocalForDisplay(
                                                  state.selectedRange,
                                              )
                                            : undefined
                                    }
                                    onSelect={(range) =>
                                        logic.handleCalendarSelect(
                                            range || undefined,
                                        )
                                    }
                                    fromYear={2020}
                                    toYear={currentYear}
                                    captionLayout="dropdown"
                                    numberOfMonths={1}
                                    showOutsideDays
                                    fixedWeeks
                                    month={
                                        state.selectedRange &&
                                        'from' in state.selectedRange &&
                                        state.selectedRange.from
                                            ? UTCToLocalForDisplay(
                                                  state.selectedRange.from,
                                              )
                                            : new Date()
                                    }
                                    classNames={customClassNames}
                                    components={{
                                        DayButton: ({
                                            children,
                                            day,
                                            modifiers,
                                            ...delegated
                                        }) => {
                                            const isSelected =
                                                !!modifiers?.selected
                                            const isRangeStart =
                                                !!modifiers?.range_start
                                            const isRangeEnd =
                                                !!modifiers?.range_end
                                            const isInRange =
                                                !!modifiers?.range_middle
                                            const isDisabled =
                                                !!modifiers?.disabled
                                            const isToday = !!modifiers?.today

                                            return (
                                                <StyledDayButton
                                                    {...delegated}
                                                    isSelected={isSelected}
                                                    isRangeStart={isRangeStart}
                                                    isRangeEnd={isRangeEnd}
                                                    isInRange={isInRange}
                                                    isDisabled={isDisabled}
                                                    isToday={isToday}
                                                >
                                                    {children}
                                                </StyledDayButton>
                                            )
                                        },
                                    }}
                                />
                            )}
                        </CalendarWrapper>
                        <HelpSection>
                            <HelpTitle>How to Filter</HelpTitle>
                            <HelpContent>
                                2024 <SubText>(all 2024)</SubText> <br />
                                202410 <SubText>(Oct 2024)</SubText>
                                <br />
                                202410-202412{' '}
                                <SubText>(Oct 2024 to Dec 2024)</SubText>
                                <br />
                                20241005 <SubText>(Oct 5th 2024)</SubText>
                            </HelpContent>
                        </HelpSection>
                    </CalendarContent>
                }
            />
        </DatePickerContainer>
    )
})

const CalendarContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: ${({ theme }) => theme.spacing[3]};
    gap: ${({ theme }) => theme.spacing[6]};

    // NEW: Nested styles targeting our custom classNames
    // These override defaults for hover, selection, etc.
    .custom-rdp-root {
        font-family: ${({ theme }) =>
            theme.typography.fontFamily}; // Match app fonts
        --rdp-cell-size: 40px; // Custom cell size (adjust as needed)
    }

    .custom-rdp-caption {
        color: ${({ theme }) => theme.colors.greyScale[100]};
        font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
        margin-bottom: ${({ theme }) => theme.spacing[2]};
    }

    .custom-rdp-nav-btn {
        background: none;
        border: none;
        color: ${({ theme }) => theme.colors.info};
        font-size: 18px;

        border-radius: ${({ theme }) => theme.borderRadius['4xl']};
        cursor: pointer;
        &:hover {
            background-color: ${({ theme }) => theme.colors.greyScale[100]};
        }
    }

    .custom-rdp-table {
        border-collapse: separate;
        border-spacing: 0;
        border-radius: ${({ theme }) => theme.borderRadius['4xl']};
        overflow: hidden;
    }

    .custom-rdp-head-row {
        background-color: ${({ theme }) => theme.colors.greyScale[200]};
    }

    .custom-rdp-day {
        // Base styles moved to StyledDayButton
        border: 1px solid transparent;
        border-radius: ${({ theme }) => theme.borderRadius['4xl']};
        /* display: flex;
        align-items: center;
        justify-content: center; // Uncommented for centering */
        &:hover {
            background-color: ${({ theme }) => theme.colors.greyScale[300]};
            cursor: pointer;
        }
        // Remove selected and range middle rules – handled by StyledDayButton
        &.custom-rdp-day-disabled {
            color: ${({ theme }) => theme.colors.greyScale[100]};
            cursor: not-allowed;
        }
    }
    .rdp-weekday {
        text-align: center;
    }

    .rdp-month {
        gap: 20px;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .custom-rdp-nav {
        justify-self: center;
        height: 30px;
        right: 0;
        position: absolute;
    }

    .rdp-dropdowns {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        gap: 20px;
    }

    .rdp-dropdown_root {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 10px;
    }

    .rdp-caption_label {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        gap: 10px;
        outline: none;
    }
    .rdp-selected {
        background-color: ${({ theme }) => theme.colors.greyScale[200]};
    }

    .custom-rdp-day.rdp-selected.rdp-range_start.rdp-range_end {
        background-color: ${({ theme }) => theme.colors.greyScale[200]};
        border-radius: 200px;
    }
`

// NEW: Styled wrapper for DayPicker – sets CSS variables from theme
const CalendarWrapper = styled.div`
    // Core colors (keep existing)
    --rdp-background: ${({ theme }) =>
        theme.colors.background?.bg1 || '#ffffff'};
    --rdp-color: ${({ theme }) => theme.colors.text.primary};
    --rdp-color-font: ${({ theme }) => theme.colors.text.primary};
    --rdp-border: ${({ theme }) => theme.colors.border.primary};
    --rdp-day-border: ${({ theme }) => theme.colors.border.primary};

    // Corrected selection variables per docs
    --rdp-range_middle-background-color: ${({ theme }) =>
        theme.colors.greyScale[300]};
    --rdp-range_start-background: ${({ theme }) => theme.colors.greyScale[200]};
    --rdp-range_end-background: ${({ theme }) => theme.colors.greyScale[200]};
    --rpd-range_start-borderRadius: ${({ theme }) => theme.borderRadius['4xl']};
    --rdp-selected-background-color: ${({ theme }) =>
        theme.colors.greyScale[200]};
    --rdp-selected-color: ${({ theme }) => theme.colors.greyScale[100]};
    --rdp-range_middle-color: ${({ theme }) => theme.colors.greyScale[100]};
    --rdp-today-color: ${({ theme }) => theme.colors.text.primary};
    --rdp-day_button-height: 42px;
    --rdp-day_button-width: 42px;
    --rdp-accent-color: ${({ theme }) => theme.colors.info};
    // Spacing
    --rdp-gap: ${({ theme }) => theme.spacing[2]};
    --rdp-dropdown-gap: ${({ theme }) => theme.spacing[1]};

    // Dark mode adapts via theme
`

const DatePickerContainer = styled.div<{ width?: string }>`
    position: relative;
    width: ${({ width }) => width};
`
